import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone } from '@fortawesome/free-solid-svg-icons';

import speechRecognitionManager from '../utils/speechRecognitionManager';

import '../styles/VoiceTextField.css';

/**
 * Click-to-toggle microphone (Greek dictation).
 * Only the active mic stays red; stops on other mic / field / submit.
 */
export const VoiceMicButton = ({
  fieldId,
  getValue,
  onValue,
  onBeforeStart,
  className = '',
  title = 'Κλικ για υπαγόρευση (Ελληνικά). Ξανά κλικ για stop.',
}) => {
  const [listening, setListening] = useState(false);
  const micRef = useRef(null);
  const autoId = useId();
  const id = fieldId || autoId;

  const getValueRef = useRef(getValue);
  const onValueRef = useRef(onValue);
  const onBeforeStartRef = useRef(onBeforeStart);
  useEffect(() => {
    getValueRef.current = getValue;
    onValueRef.current = onValue;
    onBeforeStartRef.current = onBeforeStart;
  }, [getValue, onValue, onBeforeStart]);

  // Keep red state in sync with the shared active mic id
  useEffect(() => {
    return speechRecognitionManager.subscribe((activeId) => {
      setListening(activeId === id);
    });
  }, [id]);

  useEffect(() => {
    return () => {
      if (speechRecognitionManager.isActive(id)) {
        speechRecognitionManager.stop(id);
      }
    };
  }, [id]);

  const toggleListening = useCallback(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!speechRecognitionManager.isSupported()) {
        alert('Η αναγνώριση ομιλίας δεν υποστηρίζεται. Χρησιμοποιήστε Chrome ή Edge.');
        return;
      }

      if (speechRecognitionManager.isActive(id)) {
        speechRecognitionManager.stopAll();
        return;
      }

      onBeforeStartRef.current?.();

      await speechRecognitionManager.start(id, {
        getValue: () => getValueRef.current?.() ?? '',
        onValue: (next) => onValueRef.current?.(next),
        micEl: micRef.current,
        lang: 'el-GR',
        onError: (message) => alert(message),
      });
    },
    [id]
  );

  return (
    <button
      ref={micRef}
      type="button"
      data-voice-mic="true"
      className={`voice-mic-btn ${listening ? 'is-listening' : ''} ${className}`.trim()}
      title={title}
      aria-label={title}
      aria-pressed={listening}
      onClick={toggleListening}
      onContextMenu={(e) => e.preventDefault()}
    >
      <FontAwesomeIcon icon={faMicrophone} />
      {listening && <span className="voice-mic-pulse" aria-hidden="true" />}
    </button>
  );
};

const VoiceTextField = ({
  as = 'input',
  value,
  defaultValue,
  onChange,
  className = '',
  wrapperClassName = '',
  fieldId,
  ...rest
}) => {
  const inputRef = useRef(null);
  const Tag = as === 'textarea' ? 'textarea' : 'input';
  const isControlled = value !== undefined;

  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const getValue = useCallback(() => {
    if (isControlled && typeof valueRef.current === 'string') return valueRef.current;
    return inputRef.current?.value ?? '';
  }, [isControlled]);

  const handleVoiceValue = useCallback(
    (next) => {
      valueRef.current = next;

      const target = inputRef.current;
      if (target) {
        const proto =
          Tag === 'textarea'
            ? window.HTMLTextAreaElement.prototype
            : window.HTMLInputElement.prototype;
        const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
        nativeSetter?.call(target, next);
        // Keep caret after the newly written text
        const end = next.length;
        try {
          target.setSelectionRange(end, end);
        } catch (_) {
          /* some input types don't support selection */
        }
      }

      if (typeof onChange === 'function') {
        onChange({
          target: {
            value: next,
            name: rest.name,
            type: rest.type || (Tag === 'textarea' ? 'textarea' : 'text'),
          },
          currentTarget: {
            value: next,
            name: rest.name,
          },
        });
      }
    },
    [Tag, onChange, rest.name, rest.type]
  );

  return (
    <div className={`voice-field ${wrapperClassName}`.trim()}>
      <Tag
        ref={inputRef}
        className={`voice-field-input ${className}`.trim()}
        {...(isControlled ? { value } : { defaultValue })}
        onChange={onChange}
        {...rest}
        type={as === 'textarea' ? undefined : rest.type || 'text'}
      />
      <VoiceMicButton fieldId={fieldId} getValue={getValue} onValue={handleVoiceValue} />
    </div>
  );
};

export default VoiceTextField;
